/**
 * Where everything is drawn on an office's own sheet: a template and its values
 * in, plain draw operations out (paintFormat.js puts them on a canvas).
 *
 * - A value is written on its blank: from the line's left end, just above it,
 *   turned to the line's slope (a photographed sheet is rarely square), in one
 *   size for the whole page — from the sheet's own lettering (each blank's
 *   measurement alone is too noisy: identical print reads 15 px on one row and
 *   19 px on the next). In a short blank it is shrunk to fit, dates first
 *   written short ("Apr 1/26"); never cut. What still can't fit gets a
 *   numbered note in a strip under the page ("*1"), so nothing is left out.
 * - An option ("Yes ___ No ___", "6 / 9 / 12 months") gets a check mark in the
 *   blank of the choice that is true. A known value no option matches (an
 *   18-month recall) is written in the group's last blank instead.
 * - An unknown value leaves its blank empty.
 *
 * Pure: `measure(text, sizePx)` is passed in (the canvas's own in the browser).
 */
import { factType } from "./formatFacts";
import { formatForType, formatValue } from "./formatValue";

export const INK = "#1c2f73";
export const FONT_FAMILY = "Helvetica, Arial, sans-serif";
export const font = (size) => `${Math.round(size * 10) / 10}px ${FONT_FAMILY}`;

// The sheet's lettering is measured as glyph height; a font's px size is a bit more.
const SIZE_PER_TEXT_PX = 1.3;
const SHRINK_TO = 0.6;             // never smaller than this share of the page's size
const COMFORTABLE = 0.8;           // below this, a shorter way to write it is tried first
const MIN_SIZE = 8;

/** A rough width for a string, for tests and anywhere without a canvas. */
export function estimateWidth(text, size) {
  return String(text).length * size * 0.56;
}

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

function median(list) {
  const sorted = list.slice().sort((a, b) => a - b);
  return sorted.length ? sorted[Math.floor(sorted.length / 2)] : null;
}

/** One writing size per page, from the median of its blanks' lettering. */
function pageSizes(fields, pageCount) {
  const out = [];
  for (let i = 0; i < pageCount; i += 1) {
    const heights = fields.filter((f) => f.page === i).map((f) => Number(f.text_px)).filter((v) => v > 0);
    out.push(clamp((median(heights) || 14) * SIZE_PER_TEXT_PX, 9, 64));
  }
  return out;
}

/** "Apr 1, 2026 – Mar 31, 2027" → "Apr 1/26 – Mar 31/27": the app's short date style. */
export function shortDates(text) {
  return String(text).replace(/\b([A-Z][a-z]{2} \d{1,2}), \d{2}(\d{2})\b/g, "$1/$2");
}

function geometry(field) {
  const x1 = Number(field.x1); const y1 = Number(field.y1);
  const x2 = Number(field.x2); const y2 = Number(field.y2);
  if (field.kind === "box") {
    return { box: true, x1, y1, x2, y2, length: x2 - x1, angle: 0 };
  }
  return { box: false, x1, y1, x2, y2, length: Math.hypot(x2 - x1, y2 - y1), angle: Math.atan2(y2 - y1, x2 - x1) };
}

// Along the line by `along`, then `lift` up off it (perpendicular, toward the top of the page).
function onLine(g, along, lift) {
  const c = Math.cos(g.angle); const s = Math.sin(g.angle);
  return [g.x1 + along * c + lift * s, g.y1 + along * s - lift * c];
}

function fitText(field, text, measure, full) {
  const g = geometry(field);
  const pad = g.box ? 0.15 * full : Math.min(0.3 * full, 0.08 * g.length);
  const room = Math.max(1, g.length - 2 * pad);
  const natural = measure(text, full);
  const size = natural > room ? (full * room) / natural : full;
  const width = measure(text, size);
  return { g, size, width, pad, fits: size >= Math.max(MIN_SIZE, full * SHRINK_TO) };
}

/** The way to write `text` on `field` at the largest size, or null when nothing fits. */
function bestFit(field, text, measure, full) {
  const plain = fitText(field, text, measure, full);
  if (plain.size >= full * COMFORTABLE) return { text, fit: plain };
  let best = plain.fits ? { text, fit: plain } : null;
  const short = shortDates(text);
  if (short !== text) {
    const fit = fitText(field, short, measure, full);
    if (fit.fits && (!best || fit.size > best.fit.size)) best = { text: short, fit };
  }
  return best;
}

function textOp(field, text, fit) {
  const { g, size, width, pad } = fit;
  if (g.box) {
    const x = (g.x1 + g.x2) / 2 - width / 2;
    const y = (g.y1 + g.y2) / 2 + 0.35 * size;
    return { type: "text", page: field.page, x, y, angle: 0, text, size, field: field.id };
  }
  const [x, y] = onLine(g, pad, 0.22 * size);
  return { type: "text", page: field.page, x, y, angle: g.angle, text, size, field: field.id };
}

function markOp(field, full) {
  const g = geometry(field);
  const s = clamp(full * 1.1, 10, 72);
  let base;
  let angle = g.angle;
  if (g.box) {
    base = [(g.x1 + g.x2) / 2, (g.y1 + g.y2) / 2 + 0.25 * Math.min(s, g.y2 - g.y1)];
    angle = 0;
  } else {
    base = onLine(g, Math.min(g.length / 2, Math.max(s, 0.25 * g.length)), 0.15 * s);
  }
  // A tick: down to its foot, then up and to the right. Turned with the line.
  const c = Math.cos(angle); const sn = Math.sin(angle);
  const pt = ([dx, dy]) => [base[0] + dx * c - dy * sn, base[1] + dx * sn + dy * c];
  return {
    type: "mark",
    page: field.page,
    points: [[-0.38 * s, -0.38 * s], [-0.08 * s, 0], [0.45 * s, -0.78 * s]].map(pt),
    lineWidth: Math.max(2, 0.12 * s),
    field: field.id,
  };
}

function sameChoice(markValue, value) {
  const a = String(markValue == null ? "" : markValue).trim().toLowerCase();
  const b = String(value).trim().toLowerCase();
  if (a === b) return true;
  const na = Number(a); const nb = Number(b);
  return a !== "" && b !== "" && isFinite(na) && isFinite(nb) && na === nb;
}

/** The options of one question: same row, same question, same fact. */
export function optionGroups(fields) {
  const groups = [];
  const byKey = {};
  fields.filter((f) => f.render === "mark").forEach((f) => {
    const key = `${f.page}|${f.row}|${String(f.question || "").trim().toLowerCase()}|${f.fact || ""}`;
    if (!byKey[key]) {
      byKey[key] = [];
      groups.push(byKey[key]);
    }
    byKey[key].push(f);
  });
  groups.forEach((g) => g.sort((a, b) => Number(a.x1) - Number(b.x1)));
  return groups;
}

/** What an option group says: { hit } (the true choice), { text } (a value no
 * option matches), or null (unknown). */
export function groupAnswer(group, values, { text = false } = {}) {
  const first = group.find((f) => values[f.id] !== null && values[f.id] !== undefined);
  if (!first || !first.fact) return null;
  const value = values[first.id];
  const hit = group.find((f) => sameChoice(f.mark_value, value));
  if (hit) return { hit };
  const format = formatForType(factType(first.fact));
  return { text: formatValue(value, { format }, { text }) };
}

function wrap(text, width, size, measure) {
  const words = String(text).split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";
  words.forEach((w) => {
    const next = line ? `${line} ${w}` : w;
    if (line && measure(next, size) > width) {
      lines.push(line);
      line = w;
    } else {
      line = next;
    }
  });
  if (line) lines.push(line);
  return lines;
}

function noteStrip(page, notes, sizeHint, measure) {
  if (!notes.length) return { height: 0, ops: [] };
  const size = clamp(sizeHint, 12, 40);
  const lineHeight = size * 1.4;
  const left = Math.round(page.width * 0.06);
  const width = page.width - 2 * left;
  const ops = [];
  let y = page.height + lineHeight;
  notes.forEach((n) => {
    wrap(`*${n.n}  ${n.label ? `${n.label}: ` : ""}${n.text}`, width, size, measure).forEach((line) => {
      ops.push({ type: "text", page: page.index, x: left, y, angle: 0, text: line, size, note: n.n });
      y += lineHeight;
    });
  });
  return { height: Math.ceil(y - page.height + lineHeight * 0.4), ops };
}

function cleanLabel(label) {
  return String(label || "").replace(/^[\s/]+/, "").replace(/[\s:$%]+$/, "").trim();
}

/**
 * {pages: [{index, width, height, footerHeight, ops}]}. The ops of a page are
 * in its own pixels (the cleaned background's); the note strip sits below it.
 */
export function layoutFormat(template, values, { measure = estimateWidth } = {}) {
  const vals = values || {};
  const fields = (template && template.fields) || [];
  const pages = ((template && template.pages) || []).map((p, index) => ({
    index, width: Number(p.width), height: Number(p.height), ops: [], notes: [], footerHeight: 0,
  }));
  let noteCount = 0;
  const sizes = pageSizes(fields, pages.length);

  const write = (field, text) => {
    const page = pages[field.page];
    if (!page || text === null || text === undefined || text === "") return;
    const full = sizes[field.page];
    const best = bestFit(field, text, measure, full);
    if (!best) {
      noteCount += 1;
      page.notes.push({ n: noteCount, text, label: cleanLabel(field.printed_label) || field.question || "" });
      const marker = `*${noteCount}`;
      page.ops.push(textOp(field, marker, fitText(field, marker, measure, full)));
      return;
    }
    page.ops.push(textOp(field, best.text, best.fit));
  };

  fields.forEach((field) => {
    if (field.render === "mark") return;
    write(field, formatValue(vals[field.id], field));
  });
  optionGroups(fields).forEach((group) => {
    const answer = groupAnswer(group, vals);
    if (!answer) return;
    if (answer.hit) {
      const page = pages[answer.hit.page];
      if (page) page.ops.push(markOp(answer.hit, sizes[answer.hit.page]));
    } else {
      write(group[group.length - 1], answer.text);
    }
  });

  pages.forEach((page) => {
    const strip = noteStrip(page, page.notes, sizes[page.index], measure);
    page.footerHeight = strip.height;
    page.ops = page.ops.concat(strip.ops);
  });
  return { pages };
}
